import { z } from 'zod';
import type { ValidPageRoutes } from '../routeTypes';
import type { ComponentType, BaseSyntheticEvent } from 'react';
import type { ItemSelectionData } from '../components/tasks/ItemSelectionTask/ItemSelectionTaskSchema';
import type { ReasonData } from '../components/tasks/ReasonsTask/ReasonsTaskSchema';
import type { ResolutionData } from '../components/tasks/ResolutionTask/ResolutionTaskSchema';
import type { MethodData } from '../components/tasks/MethodTask/MethodTaskSchema';
import type { RefundData } from '../components/tasks/RefundTask/RefundTaskSchema';
import type { OrderlessCustomerData } from '../components/tasks/OrderlessTask/OrderlessTaskSchema';

export type AuthStatus = 'authenticated' | 'unauthenticated';

export type Steps =
  | 'itemSelection'
  | 'reasons'
  | 'returnMethod'
  | 'review'
  | 'orderless';

// Workflow Types

export type Tasks =
  | 'itemSelection'
  | 'reason'
  | 'resolution'
  | 'returnMethod'
  | 'refund'
  | 'reviewItemSummary'
  | 'reviewRefundSummary'
  | 'returnPolicy'
  | 'signature'
  | 'orderlessCustomer';

export type TaskScope = 'cart' | 'item';

/**
 * Represents the mode of a task in the workflow.
 * - 'enabled': Task will be visible, and included in schema validation
 * - 'disabled': Task is not included and thus not validated
 * - 'implicit': Task is silent and will never be visible, but is still validated
 */
export type TaskMode = 'enabled' | 'disabled' | 'implicit';

export type TaskBaseConfig = {
  id: Tasks;
  guards?: Tasks[] | null;
  mode: TaskMode;
  scope: TaskScope;
};

export type TaskSchemaConfig<T = unknown> = {
  id: Tasks;
  schema: z.ZodType<T>;
};

export type TaskConfig = TaskBaseConfig & TaskSchemaConfig;

export type StepBaseConfig = {
  id: Steps;
  route: ValidPageRoutes;
};

export type StepConfig = StepBaseConfig & {
  tasks: TaskConfig[];
};

export type Workflow = {
  steps: StepConfig[];
};

export type TaskComponentType = ComponentType<{
  handleNext: (e?: BaseSyntheticEvent) => Promise<void>;
  itemId?: string;
  isLoading?: boolean;
}>;

export type TaskComponentMap = {
  [K in Tasks]: TaskComponentType;
};

// Workflow Data Types

type AllWorkflowDataTypes = ItemSelectionData &
  ReasonData &
  ResolutionData &
  MethodData &
  RefundData &
  OrderlessCustomerData;

export type ItemId = ItemSelectionData['itemSelection']['id'];

export type WorkflowItemData = Partial<AllWorkflowDataTypes>;

export type WorkflowData = {
  items?: Record<string, Partial<AllWorkflowDataTypes>>;
} & Partial<Omit<AllWorkflowDataTypes, 'itemSelection'>>;

export const hasItemsSelected = (data: WorkflowData | null): boolean => {
  return Boolean(data?.items && Object.keys(data.items).length > 0);
};
